/**
 * PYTHON VIEW MODAL
 * Hiển thị mã MicroPython được sinh ra từ Scratch workspace.
 * Tính năng: Syntax highlight (VS Code Dark+), line numbers, copy to clipboard, download .py
 */

export class PythonViewModal {
  constructor(options) {
    this.container = options.container;
    this.onGetCode = options.onGetCode; // () => string
    this.isOpen = false;
    this._currentCode = '';
    this.init();
  }

  init() {
    this.container.innerHTML = `
      <div class="pyview-overlay" id="pyview-overlay">
        <div class="pyview-modal" id="pyview-modal">

          <!-- Header -->
          <div class="pyview-header">
            <div class="pyview-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#569cd6" stroke-width="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
              <span>PYTHON VIEW</span>
              <span class="pyview-badge">MicroPython &bull; ESP32</span>
            </div>
            <div class="pyview-header-actions">
              <button class="pyview-action-btn" id="btn-pyview-refresh" title="Cap nhat ma moi nhat">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <polyline points="23 4 23 10 17 10"/>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                </svg>
                Cập nhật
              </button>
              <button class="pyview-action-btn" id="btn-pyview-copy" title="Sao chep toan bo code">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
                Copy
              </button>
              <button class="pyview-action-btn" id="btn-pyview-download" title="Tai xuong file .py">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Tải .py
              </button>
              <button class="pyview-close-btn" id="btn-pyview-close" title="Dong">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Stats bar -->
          <div class="pyview-stats">
            <span id="pyview-stat-lines" class="pyview-stat-lines">0 dòng</span>
            <span class="pyview-stat-sep">&middot;</span>
            <span id="pyview-stat-bytes" class="pyview-stat-bytes">0 bytes</span>
            <span class="pyview-stat-sep">&middot;</span>
            <span class="pyview-stat-lang">MicroPython 1.23</span>
          </div>

          <!-- Code area -->
          <div class="pyview-code-wrapper">
            <div class="pyview-code-container" id="pyview-code-container" style="display:none;">
              <div class="pyview-line-numbers" id="pyview-line-numbers"></div>
              <pre class="pyview-code"><code id="pyview-code-content"></code></pre>
            </div>
            <div class="pyview-empty-state" id="pyview-empty-state">
              <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" opacity="0.35">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <path d="M9 9h6M9 12h6M9 15h4"/>
              </svg>
              <p>Chưa có khối lệnh nào trong Workspace</p>
              <p class="pyview-empty-hint">Kéo thả các khối lệnh vào vùng soạn thảo để xem mã Python tương ứng.</p>
            </div>
          </div>

          <!-- Footer -->
          <div class="pyview-footer">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" opacity="0.5">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>Mã này được tự động sinh ra và sẽ nạp xuống vi điều khiển ESP32 khi bấm <strong>RUN CODE</strong>.</span>
          </div>
        </div>
      </div>
    `;

    this._injectStyles();
    this._bindEvents();
  }

  _bindEvents() {
    document.getElementById('pyview-overlay').addEventListener('click', (e) => {
      if (e.target.id === 'pyview-overlay') this.hide();
    });
    document.getElementById('btn-pyview-close').addEventListener('click', () => this.hide());
    document.getElementById('btn-pyview-refresh').addEventListener('click', () => this._loadCode());
    document.getElementById('btn-pyview-copy').addEventListener('click', () => this._copyCode());
    document.getElementById('btn-pyview-download').addEventListener('click', () => this._downloadCode());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.hide();
    });
  }

  _loadCode() {
    const code = this.onGetCode ? this.onGetCode() : '';
    const codeEl = document.getElementById('pyview-code-content');
    const lineNumEl = document.getElementById('pyview-line-numbers');
    const emptyState = document.getElementById('pyview-empty-state');
    const codeContainer = document.getElementById('pyview-code-container');

    if (!code || !code.trim()) {
      emptyState.style.display = 'flex';
      codeContainer.style.display = 'none';
      document.getElementById('pyview-stat-lines').textContent = '0 dòng';
      document.getElementById('pyview-stat-bytes').textContent = '0 bytes';
      this._currentCode = '';
      return;
    }

    this._currentCode = code;
    emptyState.style.display = 'none';
    codeContainer.style.display = 'flex';

    // Syntax highlight
    codeEl.innerHTML = this._highlight(code);

    // Line numbers
    const lines = code.split('\n');
    const lineCount = (lines[lines.length - 1].trim() === '') ? lines.length - 1 : lines.length;
    lineNumEl.innerHTML = Array.from({ length: lineCount }, (_, i) => `<span>${i + 1}</span>`).join('');

    // Stats
    document.getElementById('pyview-stat-lines').textContent = `${lineCount} dòng`;
    document.getElementById('pyview-stat-bytes').textContent = `${new TextEncoder().encode(code).length} bytes`;
  }

  _highlight(code) {
    // Step 1: Escape HTML special chars
    const esc = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Step 2: Use safe placeholder tokens
    // Dùng delimiter bọc chữ (_PH_) để regex số \b\d+\b KHÔNG BAO GIỜ match nhầm chỉ mục placeholder
    const DELIM_OPEN = '\uE000__PH_';
    const DELIM_CLOSE = '__\uE001';
    const segments = [];
    let result = esc;

    const protect = (html, cls) => {
      const idx = segments.length;
      segments.push(`<span class="${cls}">${html}</span>`);
      return `${DELIM_OPEN}${idx}${DELIM_CLOSE}`;
    };

    // Comments (phải xử lý trước string để tránh # bên trong string bị tô màu)
    result = result.replace(/(#[^\n]*)/g, (m) => protect(m, 'py-comment'));

    // Triple-quoted strings
    result = result.replace(/("""[\s\S]*?"""|'''[\s\S]*?''')/g, (m) => protect(m, 'py-string'));

    // Single/double quoted strings
    result = result.replace(/("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/g, (m) => protect(m, 'py-string'));

    // Decorators
    result = result.replace(/(@\w+)/g, (m) => protect(m, 'py-decorator'));

    // Keywords
    result = result.replace(/\b(def|class|return|if|elif|else|for|while|in|not|and|or|import|from|as|pass|break|continue|try|except|finally|raise|with|yield|global|nonlocal|lambda|True|False|None)\b/g,
      (m) => protect(m, 'py-keyword'));

    // Built-ins
    result = result.replace(/\b(int|float|str|bool|len|range|print|list|dict|tuple|set|type|isinstance|hasattr|getattr|setattr|enumerate|zip|map|filter|abs|min|max|sum|round|hex|bin|oct|input)\b/g,
      (m) => protect(m, 'py-builtin'));

    // Numbers — an toàn tuyệt đối với placeholder có dán nhãn __PH_...__
    result = result.replace(/\b(\d+\.?\d*(?:[eE][+-]?\d+)?|0x[0-9a-fA-F]+)\b/g,
      (m) => protect(m, 'py-number'));

    // Restore tất cả placeholder bằng regex match đúng delimiter
    const restoreRe = new RegExp(`${DELIM_OPEN}(\\d+)${DELIM_CLOSE}`, 'g');
    result = result.replace(restoreRe, (_, i) => segments[parseInt(i, 10)]);

    return result;
  }


  async _copyCode() {
    if (!this._currentCode) return;
    try {
      await navigator.clipboard.writeText(this._currentCode);
      const btn = document.getElementById('btn-pyview-copy');
      const original = btn.innerHTML;
      btn.innerHTML = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4ec9b0" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg> Đã sao chép!`;
      btn.style.color = '#4ec9b0';
      setTimeout(() => { btn.innerHTML = original; btn.style.color = ''; }, 2200);
    } catch (e) { console.error('Copy failed', e); }
  }

  _downloadCode() {
    if (!this._currentCode) return;
    const blob = new Blob([this._currentCode], { type: 'text/x-python' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'retocar_script.py';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  show() {
    this.isOpen = true;
    const overlay = document.getElementById('pyview-overlay');
    overlay.classList.add('pyview-visible');
    requestAnimationFrame(() => {
      document.getElementById('pyview-modal').classList.add('pyview-modal-in');
    });
    this._loadCode();
  }

  hide() {
    this.isOpen = false;
    const modal = document.getElementById('pyview-modal');
    modal.classList.remove('pyview-modal-in');
    modal.classList.add('pyview-modal-out');
    setTimeout(() => {
      document.getElementById('pyview-overlay').classList.remove('pyview-visible');
      modal.classList.remove('pyview-modal-out');
    }, 220);
  }

  toggle() {
    if (this.isOpen) this.hide();
    else this.show();
  }

  _injectStyles() {
    if (document.getElementById('pyview-styles')) return;
    const style = document.createElement('style');
    style.id = 'pyview-styles';
    style.textContent = `
      .pyview-overlay {
        position: fixed;
        inset: 0;
        z-index: 3000;
        background: rgba(0,0,0,0.65);
        backdrop-filter: blur(6px);
        display: none;
        align-items: center;
        justify-content: center;
      }
      .pyview-overlay.pyview-visible { display: flex; }

      .pyview-modal {
        width: min(920px, 96vw);
        height: min(660px, 92vh);
        background: #1e1e2e;
        border: 1px solid rgba(255,255,255,0.08);
        border-radius: 14px;
        box-shadow: 0 40px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        opacity: 0;
        transform: translateY(18px) scale(0.97);
        transition: opacity 0.22s ease, transform 0.22s cubic-bezier(0.34,1.4,0.64,1);
      }
      .pyview-modal.pyview-modal-in {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
      .pyview-modal.pyview-modal-out {
        opacity: 0;
        transform: translateY(10px) scale(0.98);
        transition: opacity 0.18s ease, transform 0.18s ease;
      }

      .pyview-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 16px 12px;
        background: #252537;
        border-bottom: 1px solid rgba(255,255,255,0.07);
        flex-shrink: 0;
      }
      .pyview-title {
        display: flex;
        align-items: center;
        gap: 10px;
        color: #e2e8f0;
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.4px;
      }
      .pyview-badge {
        font-size: 11px;
        font-weight: 600;
        padding: 2px 9px;
        border-radius: 20px;
        background: rgba(86,156,214,0.15);
        color: #569cd6;
        border: 1px solid rgba(86,156,214,0.28);
      }
      .pyview-header-actions {
        display: flex;
        align-items: center;
        gap: 5px;
      }
      .pyview-action-btn {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 5px 11px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        border: 1px solid rgba(255,255,255,0.11);
        background: rgba(255,255,255,0.05);
        color: rgba(255,255,255,0.7);
        transition: all 0.15s ease;
        font-family: inherit;
      }
      .pyview-action-btn:hover {
        background: rgba(255,255,255,0.11);
        color: #fff;
        border-color: rgba(255,255,255,0.2);
      }
      .pyview-close-btn {
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        border: 1px solid rgba(255,255,255,0.09);
        background: transparent;
        color: rgba(255,255,255,0.45);
        cursor: pointer;
        transition: all 0.15s ease;
        margin-left: 3px;
      }
      .pyview-close-btn:hover {
        background: rgba(255,80,80,0.14);
        border-color: rgba(255,80,80,0.28);
        color: #ff7b7b;
      }

      .pyview-stats {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 5px 20px;
        background: #18182a;
        font-size: 11px;
        color: rgba(255,255,255,0.38);
        font-family: 'Cascadia Code', 'Fira Code', 'JetBrains Mono', monospace;
        border-bottom: 1px solid rgba(255,255,255,0.05);
        flex-shrink: 0;
      }
      .pyview-stat-sep { opacity: 0.28; }
      .pyview-stat-lines { color: #dcdcaa; }
      .pyview-stat-bytes { color: #ce9178; }
      .pyview-stat-lang { color: #4ec9b0; }

      .pyview-code-wrapper {
        flex: 1;
        overflow: hidden;
        position: relative;
        background: #1e1e2e;
      }
      .pyview-code-container {
        height: 100%;
        overflow: auto;
        background: #1e1e2e;
      }
      .pyview-code-inner {
        display: flex;
        min-height: 100%;
      }

      .pyview-line-numbers {
        display: flex;
        flex-direction: column;
        padding: 16px 0;
        min-width: 54px;
        text-align: right;
        color: rgba(255,255,255,0.2);
        font-family: 'Cascadia Code', 'Fira Code', 'JetBrains Mono', 'Courier New', monospace;
        font-size: 13px;
        line-height: 1.72;
        background: #18182a;
        border-right: 1px solid rgba(255,255,255,0.055);
        flex-shrink: 0;
        user-select: none;
        position: sticky;
        left: 0;
        z-index: 1;
      }
      .pyview-line-numbers span {
        display: block;
        padding: 0 12px 0 6px;
      }

      .pyview-code {
        flex: 1;
        margin: 0;
        padding: 16px 24px 16px 20px;
        font-family: 'Cascadia Code', 'Fira Code', 'JetBrains Mono', 'Courier New', monospace;
        font-size: 13px;
        line-height: 1.72;
        color: #d4d4d4;
        background: transparent;
        white-space: pre;
        overflow: visible;
        tab-size: 4;
        min-width: max-content;
      }
      .pyview-code code { font-family: inherit; background: none; padding: 0; }

      /* VS Code Dark+ syntax colors */
      .py-keyword   { color: #569cd6; font-weight: 600; }
      .py-builtin   { color: #4ec9b0; }
      .py-string    { color: #ce9178; }
      .py-comment   { color: #6a9955; font-style: italic; }
      .py-number    { color: #b5cea8; }
      .py-funcname  { color: #dcdcaa; }
      .py-classname { color: #4ec9b0; font-weight: 600; }
      .py-decorator { color: #c586c0; }

      .pyview-empty-state {
        display: none;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        gap: 12px;
        color: rgba(255,255,255,0.3);
        font-size: 14px;
        text-align: center;
        padding: 40px;
      }
      .pyview-empty-state p { margin: 0; }
      .pyview-empty-hint { font-size: 12px; opacity: 0.65; max-width: 340px; line-height: 1.6; }

      .pyview-footer {
        display: flex;
        align-items: center;
        gap: 7px;
        padding: 7px 16px;
        background: #252537;
        border-top: 1px solid rgba(255,255,255,0.07);
        font-size: 11px;
        color: rgba(255,255,255,0.38);
        flex-shrink: 0;
      }
      .pyview-footer strong { color: rgba(255,255,255,0.6); }

      .pyview-code-container::-webkit-scrollbar { width: 8px; height: 8px; }
      .pyview-code-container::-webkit-scrollbar-track { background: #18182a; }
      .pyview-code-container::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.11); border-radius: 4px; }
      .pyview-code-container::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
    `;
    document.head.appendChild(style);
  }
}
